import type { GradingCompany, RawCondition, ResearchListing } from "@pokifolio/contracts";

/** A starting point for the condition check, read from the listing's own words. Always checked by a person. */
export type AssessmentSuggestion =
  | { readonly cardType: "raw"; readonly overall: RawCondition; readonly because: string }
  | {
      readonly cardType: "graded";
      readonly gradingCompany: GradingCompany;
      readonly grade: number;
      readonly because: string;
    };

const GRADED =
  /\b(PSA|BGS|Beckett|CGC|SGC)\s*(?:GEM\s*(?:MT|MINT)\s*|MINT\s*|NM-MT\s*|NM\s*)?(10|[1-9](?:\.5)?)(?![\d.])/i;
const NEAR_MINT = /\b(NM|near\s*mint|mint)\b/i;

const COMPANIES: Readonly<Record<string, GradingCompany>> = {
  psa: "psa",
  bgs: "bgs",
  beckett: "bgs",
  cgc: "cgc",
  sgc: "sgc",
};

/**
 * "PSA 10" or "BGS 9.5" in the title means a graded card; otherwise wear words ("DMG", "HP", "MP", "LP", already
 * read by the search) or "NM"/"Near Mint" give the overall condition. null when the listing says nothing useful.
 */
export function suggestAssessment(listing: ResearchListing): AssessmentSuggestion | null {
  const graded = GRADED.exec(listing.title);
  const company = graded?.[1] === undefined ? undefined : COMPANIES[graded[1].toLowerCase()];
  if (graded !== null && company !== undefined) {
    return {
      cardType: "graded",
      gradingCompany: company,
      grade: Number(graded[2]),
      because: `“${graded[0]}”`,
    };
  }
  if (listing.wear !== null)
    return { cardType: "raw", overall: listing.wear, because: "the listing’s wear words" };
  const text = `${listing.title} ${listing.condition ?? ""}`;
  const nearMint = NEAR_MINT.exec(text);
  if (nearMint !== null) return { cardType: "raw", overall: "near_mint", because: `“${nearMint[0]}”` };
  return null;
}
